import { CellProps } from 'react-table'
import { PlainHeader, sortableColumn, StackedCell, TableColumn } from 'app/components'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { HistoricoValorProfissionalList, ValorProfissionalList } from '../../core/_models'
import { ProfissionalValoresActionCell } from './ProfissionalValoresActionCell'

// Tipo e especialidade numa célula só (especialidade como apoio); os dois seguem ordenáveis.
const tipoEspecialidadeColumn = <T extends ValorProfissionalList | HistoricoValorProfissionalList>(
  t: TFunction
) =>
  sortableColumn<T>({
    id: 'tipo',
    useQueryRequest,
    fields: [
      { field: 'tipo', label: t('cols.tipo') },
      { field: 'especialidade.nome', label: t('cols.especialidade') },
    ],
    Cell: ({ row }: CellProps<T>) => (
      <StackedCell primary={row.original.tipo} secondary={row.original.especialidade?.nome} />
    ),
  })

const minimoAtendimentosColumn = <T extends ValorProfissionalList | HistoricoValorProfissionalList>(
  t: TFunction
) =>
  sortableColumn<T>({
    id: 'faturamentoMinimo',
    cellWidth: '160px',
    hideOnMobile: true,
    useQueryRequest,
    fields: [{ field: 'faturamentoMinimo', label: t('cols.minimo_atendimentos') }],
    Cell: ({ row }: CellProps<T>) => (
      <StackedCell tabular primary={row.original.faturamentoMinimo || undefined} />
    ),
  })

const valorColumn = <T extends ValorProfissionalList | HistoricoValorProfissionalList>(
  t: TFunction
) =>
  sortableColumn<T>({
    id: 'valor',
    cellWidth: '140px',
    useQueryRequest,
    fields: [{ field: 'valor', label: t('cols.valor') }],
    Cell: ({ row }: CellProps<T>) => <StackedCell tabular primary={row.original.valorFormatado} />,
  })

const valorRetornoColumn = <T extends ValorProfissionalList | HistoricoValorProfissionalList>(
  t: TFunction
) =>
  sortableColumn<T>({
    id: 'valorRetorno',
    cellWidth: '140px',
    useQueryRequest,
    fields: [{ field: 'valorRetorno', label: t('cols.valor_retorno') }],
    Cell: ({ row }: CellProps<T>) => (
      <StackedCell tabular primary={row.original.valorRetornoFormatado} />
    ),
  })

export const profissionalValoresColumns = (
  t: TFunction
): ReadonlyArray<TableColumn<ValorProfissionalList>> => [
  tipoEspecialidadeColumn<ValorProfissionalList>(t),
  minimoAtendimentosColumn<ValorProfissionalList>(t),
  valorColumn<ValorProfissionalList>(t),
  valorRetornoColumn<ValorProfissionalList>(t),
  {
    Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
    id: 'actions',
    cellWidth: '132px',
    align: 'end',
    Cell: ({ row }: CellProps<ValorProfissionalList>) => (
      <ProfissionalValoresActionCell valorProfissional={row.original} />
    ),
  },
]

export const profissionalValoresHistoricoColumns = (
  t: TFunction
): ReadonlyArray<TableColumn<HistoricoValorProfissionalList>> => [
  tipoEspecialidadeColumn<HistoricoValorProfissionalList>(t),
  valorColumn<HistoricoValorProfissionalList>(t),
  valorRetornoColumn<HistoricoValorProfissionalList>(t),
  minimoAtendimentosColumn<HistoricoValorProfissionalList>(t),
  // Vigência: início como principal, término como apoio; os dois seguem ordenáveis.
  sortableColumn<HistoricoValorProfissionalList>({
    id: 'inicio',
    cellWidth: '200px',
    useQueryRequest,
    fields: [
      { field: 'inicio', label: t('cols.inicio') },
      { field: 'termino', label: t('cols.termino') },
    ],
    Cell: ({ row }: CellProps<HistoricoValorProfissionalList>) => (
      <StackedCell
        tabular
        primary={row.original.inicioFormatado}
        secondary={
          row.original.terminoFormatado ? `até ${row.original.terminoFormatado}` : undefined
        }
      />
    ),
  }),
]
